import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template188: Aeropuertos Kioto (KIX, ITM, Haruka, CONSEJO)
// Bloques verdes (aeropuertos + consejo), bloques amarillos (notas). Ilustración viajeros a la derecha.
// Todo el contenido viene de page (backend). Colores configurables por headerBackgroundColor.
const Template188 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const introText = page?.texts?.[1]?.content;
	const kixTitle = page?.texts?.[2]?.content;
	const kixListHtml = page?.texts?.[3]?.content;
	const yellowBlock1 = page?.texts?.[4]?.content;
	const itmTitle = page?.texts?.[5]?.content;
	const itmListHtml = page?.texts?.[6]?.content;
	const yellowBlock2 = page?.texts?.[7]?.content;
	const consejoTitle = page?.texts?.[8]?.content;
	const consejoHtml = page?.texts?.[9]?.content;

	const themeColor = headerBackgroundColor || "#8EB78F";
	const greenBg = "#E8F5E9";
	const yellowBg = "rgb(255, 248, 219)";
	const yellowBorder = "rgb(245, 196, 0)";

	const illustrationSrc = getImageSrc("kansai");

	const boxGreen = {
		backgroundColor: greenBg,
		border: `3px solid rgb(61, 128, 94)`,
		borderRadius: "16px",
		padding: "16px",
	};

	const boxYellow = {
		backgroundColor: yellowBg,
		border: `2px solid ${yellowBorder}`,
		borderRadius: "16px",
		padding: "12px 14px",
		fontSize: "13px",
		color: "#444",
		fontSize: "12px",
	};

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "8px",
		marginTop: "0px",
		color: "rgb(61, 128, 94)",
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#444",
		margin: "0px",
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f5f5f5",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "10px !important" },
				"& ul": { paddingLeft: "18px", margin: "0px", fontSize: "13px" },
				"& strong": { fontWeight: 700 },
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: themeColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0px",
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: "0px",
						zIndex: 99999,
						borderRadius: "16px 16px 0 0",
					}}
				>
					{headerText}
				</Box>
			)}

			{/* INTRO */}
			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "12px 16px", fontSize: "13px", color: "#444" }}>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}

			{/* BLOQUES */}
			<Box
				data-block-id="text-2"
				sx={{
					padding: "0px 16px 20px",
					display: "flex",
					flexDirection: "column",
					gap: "16px",
					position: "relative",
				}}
			>
				{/* KIX */}
				{(kixTitle || kixListHtml) && (
					<Box data-block-id="text-3" sx={boxGreen}>
						{kixTitle && (
							<Box component="h3" sx={h3}>
								{kixTitle}
							</Box>
						)}
						{kixListHtml && (
							<Box
								component="div"
								sx={{ "& ul": { paddingLeft: "18px", margin: "0px" } }}
								dangerouslySetInnerHTML={{ __html: kixListHtml }}
							/>
						)}
					</Box>
				)}

				{/* Ilustración (entre KIX y bloque amarillo) */}
				{illustrationSrc && (
					<Box
						component="img"
						src={illustrationSrc}
						alt=""
						sx={{
							width: isSmallScreen ? "80px" : "100px",
							position: "absolute",
							top: isSmallScreen ? "140px" : "100px",
							right: "-10px",
						}}
					/>
				)}

				{/* Bloque amarillo 1 */}
				{yellowBlock1 && (
					<Box
						data-block-id="text-4"
						sx={{ ...boxYellow, marginTop: isSmallScreen ? "10px" : "0px" }}
						dangerouslySetInnerHTML={{ __html: yellowBlock1 }}
					/>
				)}

				{/* ITM */}
				{(itmTitle || itmListHtml) && (
					<Box data-block-id="text-5" sx={boxGreen}>
						{itmTitle && (
							<Box component="h3" sx={h3}>
								{itmTitle}
							</Box>
						)}
						{itmListHtml && (
							<Box
								component="div"
								sx={{ "& ul": { paddingLeft: "18px", margin: "0px" } }}
								dangerouslySetInnerHTML={{ __html: itmListHtml }}
							/>
						)}
					</Box>
				)}

				{/* Bloque amarillo 2 (Haruka) */}
				{yellowBlock2 && (
					<Box
						data-block-id="text-6"
						sx={boxYellow}
						dangerouslySetInnerHTML={{ __html: yellowBlock2 }}
					/>
				)}

				{/* CONSEJO */}
				{(consejoTitle || consejoHtml) && (
					<Box data-block-id="text-7" sx={boxGreen}>
						{consejoTitle && (
							<Box component="h3" sx={h3}>
								{consejoTitle}
							</Box>
						)}
						{consejoHtml && (
							<Box
								component="div"
								sx={{ ...p, "& p": { margin: "0px !important" } }}
								dangerouslySetInnerHTML={{ __html: consejoHtml }}
							/>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template188.thumbnail = "/images/template-1.png";

export default Template188;
